/**
 * Notion-like block model for lesson section content.
 *
 * Sections persist a single markdown `content` string (backend/API contract
 * unchanged) — this module only parses that string into editable blocks and
 * serializes blocks back to markdown. Each block is stored as exactly one
 * physical line, so parse(serialize(blocks)) === blocks and
 * serialize(parse(markdown)) round-trips losslessly for any content this
 * editor itself produced. Inline formatting (bold/italic/etc) is handled by
 * ./marks — each block's `text` is always plain, with `marks` carrying the
 * formatting ranges; only serializeBlocks turns those into markdown tokens.
 *
 * Ported from teacher-mobile/src/screens/studio/editor/richEditor/blocks.ts.
 */

import { parseInline, serializeInline } from './marks';

export const CONTINUES_SELF = ['bullet', 'numbered', 'quote'];

let counter = 0;
export const newBlockId = () => `b${Date.now().toString(36)}${(counter++).toString(36)}`;

export function parseBlocks(markdown, { legacyDollarToggle = true } = {}) {
  const lines = (markdown ?? '').split('\n');
  const blocks = [];
  for (const line of lines) {
    if (!line.trim()) continue;
    let m;
    let type;
    let rest;
    if ((m = line.match(/^####\s+(.*)$/))) { type = 'h4'; rest = m[1]; }
    else if ((m = line.match(/^###\s+(.*)$/))) { type = 'h3'; rest = m[1]; }
    else if ((m = line.match(/^##\s+(.*)$/))) { type = 'h2'; rest = m[1]; }
    else if ((m = line.match(/^#\s+(.*)$/))) { type = 'h1'; rest = m[1]; }
    else if ((m = line.match(/^>\s+(.*)$/))) { type = 'quote'; rest = m[1]; }
    else if ((m = line.match(/^[-*]\s+(.*)$/))) { type = 'bullet'; rest = m[1]; }
    else if ((m = line.match(/^\d+\.\s+(.*)$/))) { type = 'numbered'; rest = m[1]; }
    else { type = 'p'; rest = line; }
    const { text, marks } = parseInline(rest, { legacyDollarToggle });
    blocks.push({ id: newBlockId(), type, text, marks });
  }
  if (blocks.length === 0) blocks.push({ id: newBlockId(), type: 'p', text: '', marks: [] });
  return blocks;
}

const PREFIX = {
  h1: '# ', h2: '## ', h3: '### ', h4: '#### ', quote: '> ', p: '', bullet: '- ', numbered: '1. ',
};

export function serializeBlocks(blocks, { legacyDollarToggle = true } = {}) {
  let out = '';
  let numberedRun = 0;
  blocks.forEach((b, i) => {
    const prev = blocks[i - 1];
    if (i > 0) {
      const sameGroup = !!prev && prev.type === b.type && CONTINUES_SELF.includes(b.type);
      out += sameGroup ? '\n' : '\n\n';
    }
    const raw = serializeInline(b.text, b.marks, { legacyDollarToggle });
    if (b.type === 'numbered') {
      numberedRun = prev && prev.type === 'numbered' ? numberedRun + 1 : 1;
      out += `${numberedRun}. ${raw}`;
    } else {
      numberedRun = 0;
      out += `${PREFIX[b.type]}${raw}`;
    }
  });
  return out;
}

/** 1-based position of a numbered block within its consecutive run. */
export function numberedIndex(blocks, index) {
  let n = 0;
  for (let i = index; i >= 0; i--) {
    if (blocks[i].type !== 'numbered') break;
    n++;
  }
  return n;
}
